import { selectUserProjectTimeZone } from 'core/userContext';
import { useCampaignSendSize } from 'modules/campaigns/lib/hooks';
import { CampaignState } from 'modules/domains/campaign';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch, useSelector } from 'react-redux';

import { useScheduleCampaignMutation } from '../../../../lib/hooks/useScheduleCampaignMutation';
import { ModalType, useCampaignContext, useModalContext } from '../../../context';
import { useGenerateCampaignAlerts } from '../../../hooks';
import { ScheduleFormState } from '../../../types';
import {
  getErrorSchedulingCampaignMessage,
  getSuccessfullyScheduledCampaignMessage,
} from '../../../utils';
import { getScheduleCampaignPayload } from '../ScheduleCampaignModal.utils';

export const useScheduleCampaign = () => {
  const { closeModal } = useModalContext();
  const {
    campaign,
    refetch: refetchCampaign,
    estimatedSendSizeData,
    expectedSendSizeData,
  } = useCampaignContext();
  const { isLoading, mutate, error } = useScheduleCampaignMutation();
  const { generateCampaignAlerts } = useGenerateCampaignAlerts();

  const { name, campaignState, id, campaignAlertsEnabled } = campaign;

  const { sendSize } = useCampaignSendSize({ estimatedSendSizeData, expectedSendSizeData });

  const dispatch = useDispatch();
  const projectTimeZone = useSelector(selectUserProjectTimeZone);

  const isScheduledCampaign = campaignState === CampaignState.Scheduled;

  const scheduleCampaign = (values: ScheduleFormState) => {
    const onSuccess = () => {
      const message = getSuccessfullyScheduledCampaignMessage(name, isScheduledCampaign);
      dispatch(addPositiveToast(message));
      generateCampaignAlerts({ campaignAlertsEnabled, id });
      refetchCampaign();
      closeModal(ModalType.ScheduleCampaign);
    };

    const onError = (e: unknown) => {
      const errorMessage = getErrorSchedulingCampaignMessage(e);
      dispatch(addNegativeToastWithErrorId(errorMessage, 'scheduleCampaign', e));
    };

    const payload = getScheduleCampaignPayload(values, {
      campaign,
      projectTimeZone,
      sendSize: sendSize as number,
    });

    closeModal(ModalType.ConfirmScheduleCampaign);

    mutate(payload, {
      onSuccess,
      onError,
    });
  };

  return { isLoading, error, scheduleCampaign };
};
